import { useState, useEffect, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { complianceApi, type EInvoiceRecord } from '@/api/compliance.api';
import { getEInvoicePublicVerifyUrl, getGSTType } from '@/utils/gstCalculation';

interface PrintEInvoiceProps {
  ei: EInvoiceRecord;
  onClose: () => void;
}

function formatINR(n?: number | string | null): string {
  const num = Number(n);
  if (n == null || isNaN(num)) return '₹0.00';
  return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
  } catch { return String(iso); }
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${date} ${time}`;
  } catch { return String(iso); }
}

function getStateName(gstin?: string | null): string {
  if (!gstin) return 'Karnataka (29)';
  const code = gstin.length >= 2 ? gstin.substring(0, 2) : gstin;
  const map: Record<string, string> = {
    '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh',
    '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi', '08': 'Rajasthan',
    '09': 'Uttar Pradesh', '10': 'Bihar', '19': 'West Bengal', '20': 'Jharkhand',
    '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
    '27': 'Maharashtra', '29': 'Karnataka', '30': 'Goa', '32': 'Kerala',
    '33': 'Tamil Nadu', '36': 'Telangana', '37': 'Andhra Pradesh',
  };
  const name = map[code] || 'Karnataka';
  return `${name} (${code})`;
}

function amountToWords(n: number): string {
  const num = Math.round(Math.abs(n));
  if (!num) return 'Zero Rupees Only';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function inWords(n: number): string {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inWords(n % 100) : '');
    if (n < 100000) return inWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + inWords(n % 100000) : '');
    return inWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + inWords(n % 10000000) : '');
  }
  return `INR ${inWords(num)} Only`;
}

export default function PrintEInvoiceView({ ei, onClose }: PrintEInvoiceProps) {
  const [company, setCompany] = useState<{
    name: string; address: string; gstin: string;
    phone: string; email: string; pan: string; website?: string; logo_url?: string;
  } | null>(null);

  const [invoiceDetail, setInvoiceDetail] = useState<any>(null);
  const [publicVerifyDetail, setPublicVerifyDetail] = useState<any>(null);
  const [printSettings, setPrintSettings] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        
        // 1. Fetch Company Info
        const resCompany = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/v1/company/get`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const companyJson = await resCompany.json();
        if (companyJson?.data) {
          setCompany(companyJson.data);
        }

        // 2. Fetch Linked Invoice Details if available
        if (ei.invoiceId) {
          try {
            const resInv = await complianceApi.getInvoiceById(ei.invoiceId);
            if (resInv?.success && resInv?.data) {
              setInvoiceDetail(resInv.data);
            }
          } catch (e) {
            console.warn('Invoice detail fetch fallback:', e);
          }
        }

        // 3. Fetch Verification Details from public-verify endpoint
        const targetLookup = ei.irn || ei.invoiceNumber || ei.ackNumber || ei.id;
        if (targetLookup) {
          try {
            const resVerify = await fetch(
              `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/v1/compliance/e-invoice/public-verify/${encodeURIComponent(targetLookup)}`
            );
            const verifyJson = await resVerify.json();
            if (verifyJson?.success && verifyJson?.data) {
              setPublicVerifyDetail(verifyJson.data);
            }
          } catch (e) {
            console.warn('Public verify detail fetch fallback:', e);
          }
        }

        // 4. Fetch E-Invoice Customization Settings
        try {
          const resSettings = await complianceApi.getEInvoiceSettings();
          if (resSettings?.success && resSettings?.data) {
            setPrintSettings(resSettings.data);
          }
        } catch {}

      } catch (err) {
        console.error('Failed to load dynamic invoice print data:', err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [ei]);

  const parsedJson = useMemo(() => {
    try {
      return ei.signedInvoiceJson ? JSON.parse(ei.signedInvoiceJson) : null;
    } catch { return null; }
  }, [ei.signedInvoiceJson]);

  // The NIC-registered invoice — SellerDtls/BuyerDtls/ItemList/ValDtls exactly as signed
  // by the IRP (decoded server-side from the SignedInvoice JWT at generate-time). This can
  // legitimately differ from our own sales_invoice record, e.g. sandbox GSTIN harmonization —
  // it's the authoritative source for what the government actually has on file.
  const irpData = parsedJson?.decodedInvoice || null;
  const isCancelled = ei.status === 'CANCELLED';

  // Dynamic Seller Information (Derived 100% from database company profile)
  const sellerName    = company?.name    || invoiceDetail?.companyName    || publicVerifyDetail?.supplier?.name    || (ei as any).sellerName    || '—';
  const sellerGSTIN   = company?.gstin   || invoiceDetail?.companyGstin   || publicVerifyDetail?.supplier?.gstin   || (ei as any).sellerGstin   || '—';
  const sellerPAN     = company?.pan     || publicVerifyDetail?.supplier?.pan || (sellerGSTIN.length >= 12 && sellerGSTIN !== '—' ? sellerGSTIN.substring(2, 12) : '—');
  const sellerAddress = company?.address || invoiceDetail?.companyAddress || publicVerifyDetail?.supplier?.address || (ei as any).sellerAddress || '—';
  const sellerPhone   = company?.phone   || invoiceDetail?.companyPhone   || publicVerifyDetail?.supplier?.phone   || (ei as any).sellerPhone   || '';
  const sellerEmail   = company?.email   || invoiceDetail?.companyEmail   || publicVerifyDetail?.supplier?.email   || (ei as any).sellerEmail   || '';
  const sellerWebsite = company?.website || '';

  // Dynamic Buyer Information. GSTIN specifically prioritizes what NIC actually signed
  // (irpData) over our own party record — sandbox GSTIN harmonization (or any future NIC
  // normalization) can register a different GSTIN than the one on file internally, and the
  // printed e-invoice must match what the QR code cryptographically verifies, not our copy.
  const buyerGSTIN   = irpData?.BuyerDtls?.Gstin || invoiceDetail?.customerGstin || (ei as any).customerGstin || publicVerifyDetail?.buyer?.gstin || (ei as any).buyerGstin || 'URP (Unregistered)';
  const buyerName    = invoiceDetail?.customerName  || invoiceDetail?.partyName  || (ei as any).customerName || (ei as any).partyName || publicVerifyDetail?.buyer?.name || irpData?.BuyerDtls?.LglNm || irpData?.BuyerDtls?.TrdNm || '—';
  const buyerAddress = invoiceDetail?.billingAddress || (ei as any).billingAddress || publicVerifyDetail?.buyer?.address || irpData?.BuyerDtls?.Addr1 || '—';
  const buyerCity    = invoiceDetail?.billingCity    || publicVerifyDetail?.buyer?.city    || irpData?.BuyerDtls?.Loc   || '';
  const buyerState   = getStateName(buyerGSTIN !== 'URP (Unregistered)' ? buyerGSTIN : sellerGSTIN);

  const sellerStateCode = sellerGSTIN.length >= 2 ? sellerGSTIN.substring(0, 2) : '29';
  const gstType = getGSTType(sellerStateCode, buyerGSTIN);
  const isInterstate = gstType === 'IGST';

  // Dynamic Items Mapping (Derived 100% from sales_invoice_items)
  const rawItems: any[] = (invoiceDetail?.items && invoiceDetail.items.length > 0)
    ? invoiceDetail.items
    : (publicVerifyDetail?.items && publicVerifyDetail.items.length > 0)
      ? publicVerifyDetail.items
      : (irpData?.ItemList && irpData.ItemList.length > 0)
        ? irpData.ItemList
        : (ei as any).items || [];

  const itemList = rawItems.map((item: any, idx: number) => {
    const name    = item.itemName || item.name || item.PrdDesc || 'Item Description';
    const hsn     = item.hsnCode  || item.hsn  || item.HsnCd   || '—';
    const qty     = Number(item.qty || item.quantity || item.Qty || 1);
    const unit    = item.unit || item.Unit || 'NOS';
    const rate    = Number(item.rate || item.UnitPrice || (item.taxableAmount ? item.taxableAmount / qty : 0) || 0);
    const taxable = Number(item.taxableAmount || item.taxable || item.AssAmt || (rate * qty));
    const taxRate = Number(item.taxRate || item.tax_rate || item.GstRt || 0);
    const cgst    = isInterstate ? 0 : Number(item.cgst ?? item.CgstAmt ?? ((taxable * taxRate) / 200));
    const sgst    = isInterstate ? 0 : Number(item.sgst ?? item.SgstAmt ?? ((taxable * taxRate) / 200));
    const igst    = isInterstate ? Number(item.igst ?? item.IgstAmt ?? ((taxable * taxRate) / 100)) : 0;
    const total   = Number(item.total || item.TotItemVal || (taxable + cgst + sgst + igst));

    return {
      sr: idx + 1,
      name,
      hsn,
      qty,
      unit,
      rate,
      taxable,
      taxRate,
      cgst,
      sgst,
      igst,
      total,
    };
  });

  const totalTaxable = itemList.reduce((s, i) => s + (Number(i.taxable) || 0), 0);
  const totalCGST    = itemList.reduce((s, i) => s + (Number(i.cgst) || 0), 0);
  const totalSGST    = itemList.reduce((s, i) => s + (Number(i.sgst) || 0), 0);
  const totalIGST    = itemList.reduce((s, i) => s + (Number(i.igst) || 0), 0);
  const grandTotal   = Number(invoiceDetail?.grandTotal || (ei as any).totalAmount || publicVerifyDetail?.totalAmount || irpData?.ValDtls?.TotInvVal || (totalTaxable + totalCGST + totalSGST + totalIGST));

  const invoiceNumber = ei.invoiceNumber || invoiceDetail?.invoiceNumber || publicVerifyDetail?.invoiceNumber || '—';
  const invoiceDate   = formatDate(invoiceDetail?.invoiceDate || (ei as any).invoiceDate || publicVerifyDetail?.invoiceDate || ei.ackDate || ei.createdAt);
  const irnHash       = ei.irn || publicVerifyDetail?.irn || '—';
  const ackNo         = ei.ackNumber || publicVerifyDetail?.ackNumber || '—';
  const ackDate       = formatDateTime(ei.ackDate || publicVerifyDetail?.ackDate || ei.createdAt);

  // Link for the "Public Verification" button in the control bar (not part of the printed
  // document) — a human-readable convenience page for anyone who wants to check details online.
  const verifyUrl = useMemo(() => {
    return getEInvoicePublicVerifyUrl(irnHash !== '—' ? irnHash : invoiceNumber);
  }, [irnHash, invoiceNumber]);

  // The PRINTED invoice's QR must be the actual NIC-signed SignedQRCode — under GST
  // e-invoicing rules this exact signed payload is what makes a printed invoice a valid
  // e-invoice; it is not optional and cannot be substituted with a plain link. Only fall
  // back to the verify link for older records generated before this field was captured.
  const qrValue = ei.qrCodeData || verifyUrl;

  const qrSizePx = printSettings?.qrSize === 'large' ? 120 : printSettings?.qrSize === 'small' ? 90 : 105;

  return (
    <div
      className="fixed inset-0 bg-black/70 z-[60] flex items-start justify-center overflow-y-auto py-6 print:p-0 print:bg-white print:static"
      onClick={onClose}
    >
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .einv-print-area, .einv-print-area * { visibility: visible !important; }
          .einv-print-area {
            position: absolute !important; left: 0 !important; top: 0 !important;
            width: 100% !important; max-width: 100% !important;
            margin: 0 !important; padding: 0 !important;
            background: white !important; box-shadow: none !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important; color-adjust: exact !important;
          }
          @page { size: A4 portrait; margin: 8mm; }
        }
      `}</style>

      <div
        className="bg-white w-[1054px] max-w-full shadow-2xl rounded-2xl overflow-hidden print:shadow-none print:w-full print:rounded-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Control Bar */}
        <div className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-2">
            <i className="ri-file-paper-2-line text-violet-400 text-lg" />
            <span className="text-sm font-semibold">
              Print E-Invoice — {invoiceNumber}
              {isCancelled && <span className="ml-2 text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded font-semibold uppercase">Cancelled</span>}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={verifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-lg transition-colors cursor-pointer border border-slate-700"
            >
              <i className="ri-external-link-line" /> Public Verification
            </a>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#6d28d9] hover:bg-violet-700 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer shadow-sm"
            >
              <i className="ri-printer-line" /> Print Now
            </button>
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>

        {/* Printable Document */}
        <div className="einv-print-area p-[30px] print:p-0 text-[#2a2f45] font-sans text-xs bg-white flex flex-col relative">

          {/* Cancelled watermark */}
          {isCancelled && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 opacity-[0.07]">
              <span className="text-red-600 font-black text-[120px] rotate-[-30deg] select-none tracking-widest">CANCELLED</span>
            </div>
          )}

          {/* Header */}
          <div className="flex justify-between items-start">
            <div className="flex flex-col items-start gap-1">
              <h2 className="text-2xl font-black tracking-tight text-[#15235e]">{sellerName}</h2>
              <p className="text-xs text-slate-500 font-medium">Government GST E-Invoice System • IRN Registered</p>
            </div>
            <div className="text-right">
              <div className="font-sans font-extrabold text-[42px] leading-none tracking-wide text-[#6d28d9]">E-INVOICE</div>
              <div className="text-sm text-[#3a4258] mt-1 font-medium">Tax Invoice • Original for Buyer</div>
            </div>
          </div>

          {/* Accent line */}
          <div className="relative h-[3px] my-3.5 rounded-full bg-gradient-to-r from-[#15235e] via-[#6d28d9] to-[#2dd4bf]">
            <span className="absolute -right-0.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[#2dd4bf] ring-4 ring-white" />
          </div>

          {/* 3-Column Top: Seller | Invoice Grid | IRN + QR */}
          <div className="grid grid-cols-3 gap-3 mt-3">

            {/* Seller Details */}
            <div className="border border-[#e3e9fa] rounded-xl p-3.5 flex flex-col gap-2 bg-white">
              <div className="font-sans font-bold text-base text-[#15235e] mb-1">{sellerName}</div>
              {[
                { icon: 'ri-file-list-3-line', label: 'GSTIN', val: sellerGSTIN },
                { icon: 'ri-bank-card-line',   label: 'PAN',   val: sellerPAN },
                { icon: 'ri-map-pin-line',     label: 'Address', val: sellerAddress },
                { icon: 'ri-phone-line',       label: 'Phone', val: sellerPhone || '—' },
                { icon: 'ri-mail-line',        label: 'Email', val: sellerEmail || '—' },
                ...(sellerWebsite ? [{ icon: 'ri-global-line', label: 'Website', val: sellerWebsite }] : []),
              ].map(({ icon, label, val }) => (
                <div key={label} className="flex items-start gap-2 text-xs">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-[#6d28d9] to-[#4f46e5] flex items-center justify-center text-white mt-0.5">
                    <i className={`${icon} text-[9px]`} />
                  </span>
                  <span className="w-12 text-slate-500 font-medium shrink-0">{label}:</span>
                  <span className="font-bold text-[#222a44] leading-snug break-all">{val}</span>
                </div>
              ))}
            </div>

            {/* Invoice Key-Value Grid */}
            <div className="border border-[#e3e9fa] rounded-xl overflow-hidden bg-white">
              <div className="grid grid-cols-2 text-xs">
                {[
                  ['Invoice No.', invoiceNumber, false, false],
                  ['Invoice Date', invoiceDate, false, false],
                  ['IRN (Short)', irnHash ? irnHash.substring(0, 22) + '...' : '—', true, true],
                  ['Ack. No.', ackNo, true, true],
                  ['Ack. Date', ackDate, false, false],
                  ['Doc Type', publicVerifyDetail?.documentType || irpData?.DocDtls?.Typ || 'TAX INVOICE', false, false],
                  ['Supply Type', irpData?.TranDtls?.SupTyp || (buyerGSTIN && buyerGSTIN.length === 15 ? 'B2B' : 'B2C'), false, true],
                  ['Status', ei.status || publicVerifyDetail?.status || 'GENERATED', false, true],
                  ['Place of Supply', buyerState, false, false],
                  ['Tax Scheme', irpData?.TranDtls?.TaxSch || 'GST', false, false],
                  ['Generated By', ei.generatedBy || invoiceDetail?.createdBy || 'System', false, true],
                ].map(([label, val, highlight, shade], i) => (
                  <div
                    key={i}
                    className={`p-2 border-b border-[#eef1fa] ${i % 2 === 0 ? 'border-r border-[#eef1fa]' : ''} ${shade ? 'bg-[#f7f9ff]' : ''}`}
                  >
                    <span className="block text-[10px] text-slate-500 font-semibold uppercase">{label}</span>
                    <span className={`font-bold text-xs ${highlight ? 'text-[#6d28d9] font-mono break-all' : 'text-[#222a44]'}`}>{val}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* IRN Info + QR */}
            <div className="border border-[#e3e9fa] rounded-xl p-3.5 flex flex-col justify-between bg-white">
              <div className="grid grid-cols-[80px_6px_1fr] gap-y-1.5 text-xs">
                <span className="text-slate-500">Full IRN:</span>
                <span className="text-slate-400">:</span>
                <span className="text-[#6d28d9] font-mono text-[10px] break-all leading-snug font-bold">{irnHash}</span>

                <span className="text-slate-500">Ack. No.:</span>
                <span className="text-slate-400">:</span>
                <span className="text-[#222a44] font-bold">{ackNo}</span>

                <span className="text-slate-500">Ack. Date:</span>
                <span className="text-slate-400">:</span>
                <span className="text-[#222a44] font-bold">{ackDate}</span>

                <span className="text-slate-500">IRP Status:</span>
                <span className="text-slate-400">:</span>
                <span className={`font-bold ${isCancelled ? 'text-red-600' : 'text-emerald-600'}`}>
                  {isCancelled ? 'CANCELLED' : 'ACTIVE (NIC Verified)'}
                </span>
              </div>

              <div className="mt-3 pt-3 flex flex-col items-center gap-1.5 border-t border-slate-100">
                <div className="p-1.5 bg-white border border-slate-200 rounded-lg shadow-sm">
                  <QRCodeSVG value={qrValue} size={qrSizePx} level="M" />
                </div>
                <div className="text-[10px] text-[#6d28d9] font-semibold text-center leading-tight">
                  NIC-Signed QR Code — Government e-Invoice
                </div>
                {/* Plain-text link so a person reading the printed page (not scanning) can still check it —
                    the QR above is the NIC-signed payload and isn't human-readable in an ordinary camera app. */}
                <div className="text-[9px] text-slate-400 text-center break-all max-w-[160px]">
                  Verify online: {verifyUrl.replace(/^https?:\/\//, '')}
                </div>
              </div>
            </div>
          </div>

          {/* Bill To / Ship To */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            {(['BILL TO', 'SHIP TO'] as const).map((label) => (
              <div key={label} className="border border-[#e3e9fa] rounded-xl p-3.5 bg-white">
                <div className="font-sans font-bold text-[10px] tracking-wider text-[#6d28d9] uppercase mb-1.5">{label}</div>
                <div className="font-sans font-bold text-sm text-[#15235e] mb-1.5">{buyerName}</div>
                <div className="text-xs text-slate-600 leading-relaxed mb-2">
                  {label === 'SHIP TO' ? (invoiceDetail?.shippingAddress || buyerAddress) : buyerAddress}
                  {buyerCity ? `, ${buyerCity}` : ''}
                </div>
                <div className="grid grid-cols-[80px_6px_1fr] gap-y-1 text-xs">
                  <span className="text-slate-600 font-medium">GSTIN:</span><span className="text-slate-400">:</span><span className="font-semibold text-[#222a44]">{buyerGSTIN}</span>
                  <span className="text-slate-600 font-medium">State:</span><span className="text-slate-400">:</span><span className="font-semibold text-[#222a44]">{buyerState}</span>
                  <span className="text-slate-600 font-medium">Supply:</span><span className="text-slate-400">:</span>
                  <span className="font-semibold text-[#222a44]">{isInterstate ? 'Inter-State (IGST)' : 'Intra-State (CGST+SGST)'}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Item Table */}
          <div className="mt-3 border border-[#e3e9fa] rounded-xl overflow-hidden bg-white">
            <table className="w-full border-collapse text-[11px]">
              <thead>
                <tr className="bg-[#f3eeff] text-[#6d28d9] font-bold text-center border-b border-[#ddd6fe]">
                  <th className="p-2 w-8 border-r border-[#ddd6fe]">S.N.</th>
                  <th className="p-2 text-left border-r border-[#ddd6fe]">Product / Description</th>
                  <th className="p-2 w-16 border-r border-[#ddd6fe]">HSN</th>
                  <th className="p-2 w-10 border-r border-[#ddd6fe]">Qty</th>
                  <th className="p-2 w-10 border-r border-[#ddd6fe]">Unit</th>
                  <th className="p-2 w-20 text-right border-r border-[#ddd6fe]">Rate (₹)</th>
                  <th className="p-2 w-24 text-right border-r border-[#ddd6fe]">Taxable (₹)</th>
                  <th className="p-1 w-10 border-r border-[#ddd6fe]">GST%</th>
                  {isInterstate ? (
                    <th className="p-2 w-22 text-right border-r border-[#ddd6fe]">IGST (₹)</th>
                  ) : (
                    <>
                      <th className="p-2 w-20 text-right border-r border-[#ddd6fe]">CGST (₹)</th>
                      <th className="p-2 w-20 text-right border-r border-[#ddd6fe]">SGST (₹)</th>
                    </>
                  )}
                  <th className="p-2 w-24 text-right">Total (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eef1fa]">
                {itemList.length === 0 ? (
                  <tr>
                    <td colSpan={isInterstate ? 9 : 10} className="p-4 text-center text-slate-400">
                      No line items recorded for this invoice
                    </td>
                  </tr>
                ) : (
                  itemList.map((item: any, idx: number) => {
                    return (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="p-2 text-center border-r border-[#f0f3fb]">{idx + 1}</td>
                        <td className="p-2 text-left font-semibold text-[#222a44] border-r border-[#f0f3fb]">{item.name}</td>
                        <td className="p-2 text-center border-r border-[#f0f3fb] font-mono">{item.hsn}</td>
                      <td className="p-2 text-center font-bold border-r border-[#f0f3fb]">{item.qty}</td>
                      <td className="p-2 text-center border-r border-[#f0f3fb]">{item.unit}</td>
                      <td className="p-2 text-right border-r border-[#f0f3fb]">{Number(item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td className="p-2 text-right font-medium border-r border-[#f0f3fb]">{formatINR(item.taxable)}</td>
                      <td className="p-1 text-center border-r border-[#f0f3fb]">{item.taxRate}%</td>
                      {isInterstate ? (
                        <td className="p-2 text-right border-r border-[#f0f3fb]">{formatINR(item.igst)}</td>
                      ) : (
                        <>
                          <td className="p-2 text-right border-r border-[#f0f3fb]">{formatINR(item.cgst)}</td>
                          <td className="p-2 text-right border-r border-[#f0f3fb]">{formatINR(item.sgst)}</td>
                        </>
                      )}
                      <td className="p-2 text-right font-bold text-[#15235e]">{formatINR(item.total)}</td>
                    </tr>
                  );
                })
              )}
              </tbody>
              <tfoot>
                <tr className="bg-[#f3eeff] font-bold text-[#6d28d9] border-t-2 border-[#ddd6fe]">
                  <td colSpan={6} className="p-2 text-right border-r border-[#ddd6fe]">Total:</td>
                  <td className="p-2 text-right border-r border-[#ddd6fe]">{formatINR(totalTaxable)}</td>
                  <td className="p-1 border-r border-[#ddd6fe]"></td>
                  {isInterstate ? (
                    <td className="p-2 text-right border-r border-[#ddd6fe]">{formatINR(totalIGST)}</td>
                  ) : (
                    <>
                      <td className="p-2 text-right border-r border-[#ddd6fe]">{formatINR(totalCGST)}</td>
                      <td className="p-2 text-right border-r border-[#ddd6fe]">{formatINR(totalSGST)}</td>
                    </>
                  )}
                  <td className="p-2 text-right font-extrabold text-[#6d28d9]">{formatINR(grandTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Grand Total Banner */}
          <div className="mt-3 p-3 bg-[#f5f3ff] border border-[#ddd6fe] rounded-xl flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="text-xs">
              <span className="font-bold text-slate-600 uppercase text-[10px] tracking-wide block">Total Amount in Words:</span>
              <span className="font-extrabold text-[#15235e] text-xs">{amountToWords(grandTotal)}</span>
            </div>
            <div className="text-right shrink-0">
              <span className="text-[10px] text-slate-500 font-semibold uppercase block">Total Invoice Value (Incl. GST):</span>
              <span className="text-lg font-black text-[#6d28d9]">{formatINR(grandTotal)}</span>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-slate-200 flex items-end justify-between text-xs text-slate-500">
            <div>
              <p className="font-bold text-[#15235e] mb-1">Declaration:</p>
              <p className="text-[11px] leading-relaxed">
                We declare that this invoice shows the actual price of the goods / services described and that all particulars are true and correct.
                This E-Invoice is digitally verified by the Government IRP (Invoice Registration Portal).
              </p>
              <p className="mt-1 text-[10px] text-slate-400">
                IRN Generated By: {ei.generatedBy || invoiceDetail?.createdBy || 'System'} &nbsp;|&nbsp; Ack. No.: {ackNo} &nbsp;|&nbsp; Ack. Date: {ackDate}
              </p>
            </div>
            <div className="text-center shrink-0 min-w-[200px]">
              <div className="h-12 border-b border-slate-400 mb-1" />
              <p className="font-bold text-[#15235e]">{sellerName}</p>
              <p className="text-[10px] text-slate-500">Authorized Signatory</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
